import superjson from 'superjson'
import { TableColumn } from './types'

/**
 * Bump this when a change to column semantics should invalidate previously persisted
 * layouts (forcing every user back to the code defaults). Generalises the manual
 * `tableKey` renames some tables do today.
 */
export const COLUMN_STATE_VERSION = 1

interface PersistedColumnState {
  v: number
  /** columnKey -> visible. The only user-controlled state we persist. */
  visibility: Record<string, boolean>
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const toVisibilityMap = <T>(columns: TableColumn<T>[]): Record<string, boolean> =>
  Object.fromEntries(columns.map(col => [String(col.key), col.visible]))

const toPersistedColumnState = <T>(columns: TableColumn<T>[]): PersistedColumnState => ({
  v: COLUMN_STATE_VERSION,
  visibility: toVisibilityMap(columns),
})

/**
 * Rebuilds render-ready columns from the current code definitions (`freshColumns`),
 * applying persisted visibility by key. This is the resilient core:
 * - columns added in a new app version appear automatically (their code default),
 * - columns removed/renamed in code are dropped (stale keys are ignored),
 * - column order always follows the code (users cannot reorder, so we never persist it),
 * - if the persisted state would hide every column, we fall back to the defaults so the
 *   table can never render empty.
 */
export const applyColumnVisibility = <T>(
  freshColumns: TableColumn<T>[],
  visibility: Record<string, boolean>
): TableColumn<T>[] => {
  const applied = freshColumns.map(col => {
    const stored = visibility[String(col.key)]
    return typeof stored === 'boolean' ? { ...col, visible: stored } : col
  })
  return applied.some(col => col.visible) ? applied : freshColumns
}

/** Re-derives columns from fresh definitions while preserving the current visibility state. */
export const syncColumns = <T>(freshColumns: TableColumn<T>[], currentColumns: TableColumn<T>[]): TableColumn<T>[] =>
  applyColumnVisibility(freshColumns, toVisibilityMap(currentColumns))

const visibilityFromLegacyArray = (parsed: unknown[]): Record<string, boolean> => {
  const visibility: Record<string, boolean> = {}
  for (const col of parsed) {
    if (isRecord(col) && 'key' in col && typeof col.visible === 'boolean') {
      visibility[String(col.key)] = col.visible
    }
  }
  return visibility
}

/** Extracts a visibility map from a parsed payload, migrating the legacy array format. */
const parseVisibility = (parsed: unknown): Record<string, boolean> | null => {
  // Legacy format (pre-versioning): the full column array was stored.
  if (Array.isArray(parsed)) return visibilityFromLegacyArray(parsed)

  // Current format: { v, visibility }. A version mismatch resets to defaults.
  if (isRecord(parsed) && parsed.v === COLUMN_STATE_VERSION && isRecord(parsed.visibility)) {
    const visibility: Record<string, boolean> = {}
    for (const [key, val] of Object.entries(parsed.visibility)) {
      if (typeof val === 'boolean') visibility[key] = val
    }
    return visibility
  }

  return null
}

export const serializeColumns = <T>(columns: TableColumn<T>[]): string =>
  superjson.stringify(toPersistedColumnState(columns))

/**
 * Reads persisted column state and rebuilds columns from the code definitions.
 * Any failure (corrupt value, incompatible format, version mismatch) falls back to the
 * code defaults rather than crashing the table.
 */
export const deserializeColumns = <T>(value: string | undefined, columns: TableColumn<T>[]): TableColumn<T>[] => {
  if (!value) return columns
  try {
    const visibility = parseVisibility(superjson.parse(value))
    return visibility ? applyColumnVisibility(columns, visibility) : columns
  } catch {
    return columns
  }
}
